/**
 * WebSocket AUTH_ERROR recovery — refresh-first, logout-last.
 *
 * When the backend rejects the live socket's access token (AUTH_ERROR with
 * TOKEN_EXPIRED / TOKEN_INVALID), the resilient behavior is the SAME as the
 * HTTP 401 path: try to mint a fresh access token from the refresh token
 * BEFORE tearing down the session. Only if that genuinely fails do we log out
 * — and that logout is owned centrally by `TokenService.refreshToken()`
 * (it clears storage and redirects to /login on definitive failure), so this
 * module must never issue its own redirect. Doing so here would race the
 * centralized handler and reintroduce the asymmetry we are removing.
 *
 * The logic is dependency-injected so it can be unit-tested without dragging
 * in the full websocket.actions module graph.
 */

import { createLogger } from '$lib/utils/logger';

const log = createLogger('ws-auth-recovery');

export interface WsAuthRecoveryDeps {
	/**
	 * Attempt a token refresh. Returns true on success. On definitive failure
	 * it is expected to have ALREADY performed centralized logout (storage
	 * clear + redirect) — this is exactly the contract of
	 * `TokenService.refreshToken()`.
	 */
	refreshToken: () => Promise<boolean>;
	/** Reconnect the socket so it re-authenticates with the new token. */
	reconnect: () => Promise<void>;
}

/**
 * Recover a WebSocket session after an AUTH_ERROR.
 *
 * @returns true if the session was recovered (refreshed + reconnected),
 *          false if the refresh failed (logout already handled downstream)
 *          or the reconnect could not complete.
 */
export async function recoverFromWsAuthError(deps: WsAuthRecoveryDeps): Promise<boolean> {
	let refreshed = false;
	try {
		refreshed = await deps.refreshToken();
	} catch (err) {
		log.error('Token refresh threw during WS auth recovery', {
			error: err instanceof Error ? err.message : String(err)
		});
		refreshed = false;
	}

	if (!refreshed) {
		// TokenService.refreshToken() has already cleared storage and redirected
		// to /login on definitive failure. Nothing more to do here.
		log.warn('WS auth recovery failed — refresh unsuccessful; logout handled centrally');
		return false;
	}

	// The server rejected the OLD token on the open socket, so the existing
	// connection is dead even though we now hold a fresh token. Reconnect to
	// re-authenticate with it.
	try {
		await deps.reconnect();
		log.info('WS auth recovered: token refreshed and socket reconnected');
		return true;
	} catch (err) {
		log.error('Reconnect after token refresh failed', {
			error: err instanceof Error ? err.message : String(err)
		});
		return false;
	}
}
